import { IConfiguration, IUserData } from '../SuiteHeader.types';
import { IResourceResponse, IResourceTenant, ITenantInfoPayload } from '../types/ResourceTenant.types';
import { processAltSecIdClaim } from '../utils/AltSecIdClaimHelper';
import { Buffer } from 'buffer';

export const getToken = async (url: string, getTokenFunc: (baseUrl: string) => Promise<string | null>) => {
  const token = await getTokenFunc(url);
  if (!token) {
    throw new Error(`error on getting token`);
  }
  return token;
};

export async function fetchResourceTenants(
  configuration: IConfiguration,
  userData: IUserData
): Promise<IResourceTenant[]> {
  const homeTenantId = userData?.homeTenantId;
  const altSecId: string = (userData && (userData?.rawClaims?.altsecid as string)) ?? '';
  const puid: string = (userData && (userData?.rawClaims?.puid as string)) ?? null;
  const { securityIdentityType, identityProvider, identifier } = processAltSecIdClaim(altSecId, puid);
  const buffer = Buffer.from(identifier ?? '', 'hex');
  const payload = {
    alternativeSecurityId: {
      type: securityIdentityType,
      identityProvider: identityProvider,
      key: buffer.toString('base64')
    },
    homeTenantId: homeTenantId
  };
  const msgraphEndpoint = configuration.msGraphBaseUrl;
  // Client is granted access by requesting directory tasksets and hence no specific scope is really required, but using CrossTenantInformation.ReadBasic.All instead of /.default
  // to avoid Conditional access policy from evaluating the token request against all preauthorized scopes
  const scope = `${msgraphEndpoint}/CrossTenantInformation.ReadBasic.All`;
  const token = await getToken(scope, configuration.getToken);
  const response = await fetch(`${msgraphEndpoint}/beta/tenantRelationships/getResourceTenants?$select=tenantId`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    const responseText = await response.text();
    throw new Error(
      `${response.status}: Failed to get information of tenants the user is part of  ${responseText}, User altsecId ${altSecId}`
    );
  }
  const result = (await response.json()) as IResourceResponse;
  return result.value;
}

export async function fetchTenantName(tenantId: string, configuration: IConfiguration): Promise<ITenantInfoPayload> {
  const msgraphEndpoint = configuration.msGraphBaseUrl;
  const scope = `${msgraphEndpoint}/CrossTenantInformation.ReadBasic.All`;
  const token = await getToken(scope, configuration.getToken);
  const response = await fetch(
    `${msgraphEndpoint}/beta/tenantRelationships/findTenantInformationByTenantId(tenantId='${tenantId}')`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  if (!response.ok) {
    const responseText = await response.text();
    throw new Error(
      `${response.status}: Failed to get tenant information for tenantId=${tenantId} with error ${responseText}`
    );
  }

  const result = (await response.json()) as ITenantInfoPayload;
  return result;
}
